import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { KitCard, GroupLabel, KitButton, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitChip,
    KitChips,
    KitEntityCard,
    KitField,
    KitFab,
    KitInput,
    KitSearch,
    KitSelect,
    KitStickyToolbar,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { APP_BAR_OFFSET } from '../layout/mobileShell';

/**
 * Lista "Minhas vendas" no shell mobile — `prototipos-mobile/vendas/lista.html`.
 * Só apresentação: filtros, paginação e navegação vêm de `pages/sales/List.js`.
 */

const CalendarIcon = protoIcon('calendar');
const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const ReceiptIcon = protoIcon('receipt');

// Rótulos do protótipo (a API devolve "Finalizada" etc.) + cor da faixa lateral/pill.
const STATUS_VIEW = {
    iniciada: { label: 'Iniciada', tone: 'info', color: '#2563eb', chip: 'Iniciadas' },
    finalizada: { label: 'Concluída', tone: 'success', color: '#16a34a', chip: 'Concluídas' },
    cancelada: { label: 'Cancelada', tone: 'danger', color: '#ef4444', chip: 'Canceladas' },
    estornada: { label: 'Estornada', tone: 'warning', color: '#f59e0b', chip: 'Estornadas' },
};

const STATUS_CHIPS = [
    { value: '', label: 'Todas' },
    { value: 'Finalizada', label: 'Concluídas' },
    { value: 'Cancelada', label: 'Canceladas' },
    { value: 'Estornada', label: 'Estornadas' },
    { value: 'Iniciada', label: 'Iniciadas' },
];

const statusView = (status) => STATUS_VIEW[String(status || '').trim().toLowerCase()] || { label: status || '—', tone: undefined, color: '#94a3b8' };

// "17/10/2026 14:32" -> { day: '17/10/2026', time: '14:32' }
function splitDate(value) {
    const text = String(value || '');
    return { day: text.slice(0, 10), time: text.slice(11, 16) };
}

function dayLabel(day) {
    const parsed = dayjs(day, 'DD/MM/YYYY', true);
    if (!parsed.isValid()) return day || '—';
    const today = dayjs().startOf('day');
    if (parsed.isSame(today, 'day')) return 'Hoje';
    if (parsed.isSame(today.subtract(1, 'day'), 'day')) return 'Ontem';
    return day;
}

function periodLabel(period) {
    const { start, end } = period;
    const hasStart = start && start.isValid();
    const hasEnd = end && end.isValid();
    if (!hasStart && !hasEnd) return 'Todas as datas';
    const today = dayjs().startOf('day');
    if (hasStart && hasEnd) {
        if (start.isSame(end, 'day')) return start.isSame(today, 'day') ? 'Hoje' : start.format('DD/MM/YYYY');
        if (start.isSame(today.startOf('month'), 'day') && end.isSame(today.endOf('month'), 'day')) return 'Este mês';
        if (end.isSame(today, 'day') && today.diff(start, 'day') === 7) return '7 dias';
        if (end.isSame(today, 'day') && today.diff(start, 'day') === 30) return '30 dias';
        return `${start.format('DD/MM')} – ${end.format('DD/MM')}`;
    }
    return hasStart ? `A partir de ${start.format('DD/MM/YYYY')}` : `Até ${end.format('DD/MM/YYYY')}`;
}

function presetRange(key) {
    const today = dayjs().startOf('day');
    switch (key) {
        case 'today': return { start: today, end: today };
        case '7': return { start: today.subtract(7, 'day'), end: today };
        case '30': return { start: today.subtract(30, 'day'), end: today };
        case 'month': return { start: today.startOf('month'), end: today.endOf('month').startOf('day') };
        default: return null;
    }
}

const PRESETS = [
    { key: 'today', label: 'Hoje' },
    { key: '7', label: '7 dias' },
    { key: '30', label: '30 dias' },
    { key: 'month', label: 'Mês' },
    { key: 'custom', label: 'Personalizado' },
];

function activePreset(start, end) {
    if (!start || !end) return 'custom';
    const found = PRESETS.find((preset) => {
        const range = presetRange(preset.key);
        return range && range.start.isSame(start, 'day') && range.end.isSame(end, 'day');
    });
    return found ? found.key : 'custom';
}

const toInput = (value) => (value && value.isValid() ? value.format('YYYY-MM-DD') : '');
const fromInput = (text) => {
    if (!text) return null;
    const parsed = dayjs(text, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
};

function SaleCard({ sale, finalTotal, onOpen }) {
    const view = statusView(sale.status);
    const { time } = splitDate(sale.created_date);
    return (
        <KitEntityCard stripeColor={view.color} onClick={() => onOpen(sale.id)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: '-.01em' }}>#{sale.number}</Box>
                <KitPill tone={view.tone}>{view.label}</KitPill>
            </Box>
            <Box sx={{ m: '8px 0 10px' }}>
                <Box sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.4, color: 'text.primary' }}>
                    {sale.customer_name || 'Consumidor final'}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <CalendarIcon size={13} />{time || '—'}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', mt: '12px', pt: '10px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '-.02em' }}>R$ {finalTotal}</Box>
                <Box component="span" sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.4 }}>{sale.seller || 'Balcão'}</Box>
            </Box>
        </KitEntityCard>
    );
}

export default function MobileSalesList({
    sales,
    loading,
    totalCount,
    amountTotal,
    hasMore,
    onLoadMore,
    status,
    onStatusChange,
    search,
    onSearchChange,
    appliedPeriod,
    onApplyPeriod,
    onClearFilters,
    statusOptions,
    canCreate,
    onNew,
    onOpen,
    getFinalTotal,
}) {
    const theme = useTheme();
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [periodOpen, setPeriodOpen] = useState(false);
    const [draftStatus, setDraftStatus] = useState(status);
    const [draftStart, setDraftStart] = useState(appliedPeriod.start);
    const [draftEnd, setDraftEnd] = useState(appliedPeriod.end);

    const openPeriod = () => {
        setDraftStart(appliedPeriod.start);
        setDraftEnd(appliedPeriod.end);
        setPeriodOpen(true);
    };

    useAppBarAction({ label: 'Período', icon: CalendarIcon, onClick: openPeriod });

    const groups = useMemo(() => {
        const result = [];
        sales.forEach((sale) => {
            const { day } = splitDate(sale.created_date);
            const last = result[result.length - 1];
            if (last && last.day === day) last.items.push(sale);
            else result.push({ day, items: [sale] });
        });
        return result;
    }, [sales]);

    const draftPreset = activePreset(draftStart, draftEnd);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={APP_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Número da venda…"
                    inputProps={{ 'aria-label': 'Buscar venda pelo número', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton
                    aria-label="Filtros"
                    onClick={() => {
                        setDraftStatus(status);
                        setFiltersOpen(true);
                    }}
                >
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <KitChips>
                {STATUS_CHIPS.map((chip) => (
                    <KitChip
                        key={chip.value || 'all'}
                        active={status === chip.value}
                        count={status === chip.value && !loading ? totalCount : undefined}
                        onClick={() => onStatusChange(chip.value)}
                    >
                        {chip.label}
                    </KitChip>
                ))}
            </KitChips>

            <KitCard
                sx={{
                    mt: '12px',
                    backgroundImage: theme.palette.gradient.brand,
                    color: '#fff',
                    border: 0,
                    boxShadow: theme.customShadows.md,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                }}
            >
                <Box>
                    <Box sx={{ fontSize: 12.5, opacity: 0.85, lineHeight: 1.4 }}>Total no período</Box>
                    <Box sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.4 }}>R$ {amountTotal || '0,00'}</Box>
                </Box>
                <KitPill sx={{ backgroundColor: 'rgba(255,255,255,.2)', color: '#fff', '&::before': { display: 'none' } }}>
                    {periodLabel(appliedPeriod)}
                </KitPill>
            </KitCard>

            {loading && sales.length === 0 && (
                <Box sx={{ mt: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={132} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && sales.length === 0 && (
                <Box sx={{ textAlign: 'center', p: '36px 20px', color: 'text.secondary' }}>
                    <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', m: '0 auto 14px' }}>
                        <ReceiptIcon size={28} />
                    </Box>
                    <Typography component="h3" sx={{ fontSize: 16, fontWeight: 800, mb: '4px', color: 'text.primary', lineHeight: 1.4 }}>Nenhuma venda encontrada</Typography>
                    <Typography sx={{ fontSize: 13, mb: '16px', lineHeight: 1.4 }}>Ajuste o período ou os filtros para ver outras vendas.</Typography>
                </Box>
            )}

            {groups.map((group, groupIndex) => (
                <Box key={`${group.day}-${groupIndex}`}>
                    <GroupLabel>{dayLabel(group.day)}</GroupLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {group.items.map((sale) => (
                            <SaleCard key={sale.id} sale={sale} finalTotal={getFinalTotal(sale)} onOpen={onOpen} />
                        ))}
                    </Box>
                </Box>
            ))}

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {canCreate && <KitFab label="Nova venda" icon={PlusIcon} onClick={onNew} />}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onClearFilters();
                            }}
                        >
                            Limpar
                        </KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onStatusChange(draftStatus);
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Status">
                    <KitSelect value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)} aria-label="Status">
                        <option value="">Todos</option>
                        {statusOptions.map((option) => (
                            <option key={option} value={option}>{statusView(option).label}</option>
                        ))}
                    </KitSelect>
                </KitField>
            </MobileSheet>

            <MobileSheet
                open={periodOpen}
                onClose={() => setPeriodOpen(false)}
                title="Período"
                footer={(
                    <KitButton
                        variant="primary"
                        sx={{ flex: 1 }}
                        onClick={() => {
                            setPeriodOpen(false);
                            onApplyPeriod(draftStart, draftEnd);
                        }}
                    >
                        Aplicar
                    </KitButton>
                )}
            >
                <KitChips>
                    {PRESETS.map((preset) => (
                        <KitChip
                            key={preset.key}
                            active={draftPreset === preset.key}
                            onClick={() => {
                                const range = presetRange(preset.key);
                                if (range) {
                                    setDraftStart(range.start);
                                    setDraftEnd(range.end);
                                }
                            }}
                        >
                            {preset.label}
                        </KitChip>
                    ))}
                </KitChips>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px', mt: '12px' }}>
                    <KitField label="De">
                        <KitInput type="date" aria-label="Data inicial" value={toInput(draftStart)} onChange={(e) => setDraftStart(fromInput(e.target.value))} />
                    </KitField>
                    <KitField label="Até">
                        <KitInput type="date" aria-label="Data final" value={toInput(draftEnd)} onChange={(e) => setDraftEnd(fromInput(e.target.value))} />
                    </KitField>
                </Box>
            </MobileSheet>
        </Box>
    );
}
